import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getProfile, saveProfile } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import DashboardLayout from '../components/DashboardLayout';
import NationalitySelect from '../components/NationalitySelect';
import { Flag } from '../components/Flag';
import { Loader2, Save, User, Scale, Ruler, Activity, Calendar, Target, AlertTriangle, Bike, MapPin, Phone, Globe, Bell } from 'lucide-react';
import { toast } from 'sonner';

const CARDIO_TYPES = {
  running: 'Corrida',
  cycling: 'Ciclismo',
  swimming: 'Natação',
  rowing: 'Remo',
  hiit: 'HIIT',
  walking: 'Caminhada',
  elliptical: 'Elíptico',
  jump_rope: 'Saltar à Corda',
  stair_climbing: 'Subir Escadas',
  dancing: 'Dança'
};

const GENDERS = {
  male: 'Masculino',
  female: 'Feminino',
  other: 'Outro',
  prefer_not_say: 'Prefiro não dizer'
};

const ProfilePage = () => {
  const { user } = useAuth();
  const { setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    // Personal data
    nationality: '',
    birth_date: '',
    gender: '',
    location: '',
    phone: '',
    // Physical data
    age: '',
    weight: '',
    height: '',
    // Training preferences
    experience_level: '',
    weekly_frequency: '',
    goals: '',
    injuries: '',
    restrictions: '',
    preferred_cardio: [],
    // Notification preferences
    reminder_emails: true,
    reminder_sms: false
  });
  const [countryCode, setCountryCode] = useState('+351');
  const [localPhone, setLocalPhone] = useState('');

  const applyPhone = (value) => {
    const codes = ['+351', '+34', '+33', '+44', '+49', '+55', '+41', '+1'];
    const match = codes.find((c) => value.startsWith(c));
    if (match) {
      setCountryCode(match);
      setLocalPhone(value.slice(match.length).trim());
    } else {
      setLocalPhone(value);
    }
  };

  const handleNationalityChange = (country) => {
    setProfile((prev) => ({ ...prev, nationality: country.code }));
    // Nationality drives the app language when we have a translation for it
    if (country.lang) {
      setLanguage(country.lang);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await getProfile();
        if (data) {
          setProfile({
            nationality: data.nationality || '',
            birth_date: data.birth_date || '',
            gender: data.gender || '',
            location: data.location || '',
            phone: data.phone || '',
            age: data.age || '',
            weight: data.weight || '',
            height: data.height || '',
            experience_level: data.experience_level || '',
            weekly_frequency: data.weekly_frequency || '',
            goals: data.goals || '',
            injuries: data.injuries || '',
            restrictions: data.restrictions || '',
            preferred_cardio: data.preferred_cardio || [],
            reminder_emails: data.reminder_emails !== false,
            reminder_sms: data.reminder_sms === true
          });
          // Split the stored E.164 number into country code + local part
          if (data.phone) {
            applyPhone(data.phone);
          }
        } else if (user?.phone) {
          // No profile yet: pre-fill the phone collected at signup
          applyPhone(user.phone);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!profile.age || !profile.weight || !profile.height || !profile.experience_level || !profile.weekly_frequency || !profile.goals) {
      toast.error('Preenche todos os campos obrigatórios');
      return;
    }

    setSaving(true);
    try {
      await saveProfile({
        ...profile,
        phone: localPhone ? `${countryCode}${localPhone.replace(/\s/g, '')}` : '',
        age: parseInt(profile.age),
        weight: parseFloat(profile.weight),
        height: parseFloat(profile.height),
        weekly_frequency: parseInt(profile.weekly_frequency)
      });
      toast.success('Perfil guardado com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Erro ao guardar perfil');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#32CD32]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="profile-page">
        <div className="mb-8">
          <h1 
            className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-2"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            O Teu <span className="text-[#32CD32]">Perfil</span>
          </h1>
          <p className="text-gray-400">
            Preenche as tuas informações para receber planos personalizados
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Data Card */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
                  <User className="w-5 h-5 text-[#32CD32]" />
                  Dados Pessoais
                </CardTitle>
                <CardDescription className="text-[#A1A1AA]">Informações sobre ti</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="birth_date" className="text-[#A1A1AA] flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Data de Nascimento
                  </Label>
                  <Input
                    id="birth_date"
                    type="date"
                    value={profile.birth_date}
                    onChange={(e) => setProfile({ ...profile, birth_date: e.target.value })}
                    className="h-12 bg-black/50 border-white/20 rounded-lg"
                    data-testid="profile-birthdate-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gender" className="text-[#A1A1AA]">Género</Label>
                  <Select 
                    value={profile.gender} 
                    onValueChange={(value) => setProfile({ ...profile, gender: value })}
                  >
                    <SelectTrigger className="h-12 bg-black/50 border-white/20 rounded-lg" data-testid="profile-gender-select">
                      <SelectValue placeholder="Seleciona o teu género" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/20">
                      {Object.entries(GENDERS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location" className="text-[#A1A1AA] flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Localidade
                  </Label>
                  <Input
                    id="location"
                    type="text"
                    placeholder="Ex: Lisboa, Porto, Faro..."
                    value={profile.location}
                    onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                    className="h-12 bg-black/50 border-white/20 rounded-lg"
                    data-testid="profile-location-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-[#A1A1AA] flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Telemóvel
                  </Label>
                  <div className="flex gap-2">
                    <Select
                      value={countryCode}
                      onValueChange={setCountryCode}
                    >
                      <SelectTrigger className="h-12 w-32 bg-black/50 border-white/20 rounded-lg" data-testid="profile-country-code-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/20">
                        <SelectItem value="+351"><span className="flex items-center gap-2"><Flag code="PT" size={18} /> +351</span></SelectItem>
                        <SelectItem value="+34"><span className="flex items-center gap-2"><Flag code="ES" size={18} /> +34</span></SelectItem>
                        <SelectItem value="+33"><span className="flex items-center gap-2"><Flag code="FR" size={18} /> +33</span></SelectItem>
                        <SelectItem value="+44"><span className="flex items-center gap-2"><Flag code="GB" size={18} /> +44</span></SelectItem>
                        <SelectItem value="+49"><span className="flex items-center gap-2"><Flag code="DE" size={18} /> +49</span></SelectItem>
                        <SelectItem value="+55"><span className="flex items-center gap-2"><Flag code="BR" size={18} /> +55</span></SelectItem>
                        <SelectItem value="+1"><span className="flex items-center gap-2"><Flag code="US" size={18} /> +1</span></SelectItem>
                        <SelectItem value="+41"><span className="flex items-center gap-2"><Flag code="CH" size={18} /> +41</span></SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="912 345 678"
                      value={localPhone}
                      onChange={(e) => setLocalPhone(e.target.value)}
                      className="h-12 flex-1 bg-black/50 border-white/20 rounded-lg"
                      data-testid="profile-phone-input"
                    />
                  </div>
                  <p className="text-xs text-[#A1A1AA]">
                    Usado para lembretes por SMS (opcional)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nationality" className="text-[#A1A1AA] flex items-center gap-2">
                    <Globe className="w-4 h-4" /> {t('nationality')}
                  </Label>
                  <NationalitySelect
                    value={profile.nationality}
                    onChange={handleNationalityChange}
                    placeholder={t('selectNationality')}
                  />
                  <p className="text-xs text-[#A1A1AA]">
                    Define automaticamente o idioma da app
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Notification Preferences Card */}
            <Card className="bg-[#121212] border-white/10" data-testid="notification-preferences-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
                  <Bell className="w-5 h-5 text-[#32CD32]" />
                  Notificações
                </CardTitle>
                <CardDescription className="text-[#A1A1AA]">
                  Escolhe como queres ser lembrado de treinar
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label htmlFor="reminder_emails" className="text-white">
                      Lembretes por e-mail
                    </Label>
                    <p className="text-xs text-[#A1A1AA] mt-1">
                      Receber um incentivo quando estiveres 3 dias sem treinar
                    </p>
                  </div>
                  <Switch
                    id="reminder_emails"
                    checked={profile.reminder_emails}
                    onCheckedChange={(v) => setProfile({ ...profile, reminder_emails: v })}
                    data-testid="profile-reminder-emails-switch"
                  />
                </div>

                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label htmlFor="reminder_sms" className="text-white">
                      Lembretes por SMS
                    </Label>
                    <p className="text-xs text-[#A1A1AA] mt-1">
                      Requer telemóvel preenchido acima
                    </p>
                  </div>
                  <Switch
                    id="reminder_sms"
                    checked={profile.reminder_sms}
                    onCheckedChange={(v) => setProfile({ ...profile, reminder_sms: v })}
                    data-testid="profile-reminder-sms-switch"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Physical Info Card */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
                  <Scale className="w-5 h-5 text-[#32CD32]" />
                  Dados Físicos
                </CardTitle>
                <CardDescription className="text-[#A1A1AA]">Informações para personalizar o treino</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="age" className="text-[#A1A1AA] flex items-center gap-2">
                    <Calendar className="w-4 h-4" /> Idade *
                  </Label>
                  <Input
                    id="age"
                    type="number"
                    placeholder="25"
                    min="10"
                    max="100"
                    value={profile.age}
                    onChange={(e) => setProfile({ ...profile, age: e.target.value })}
                    className="h-12 bg-black/50 border-white/20 rounded-lg"
                    data-testid="profile-age-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="weight" className="text-[#A1A1AA] flex items-center gap-2">
                    <Scale className="w-4 h-4" /> Peso (kg) *
                  </Label>
                  <Input
                    id="weight"
                    type="number"
                    placeholder="70"
                    min="30"
                    max="300"
                    step="0.1"
                    value={profile.weight}
                    onChange={(e) => setProfile({ ...profile, weight: e.target.value })}
                    className="h-12 bg-black/50 border-white/20 rounded-lg"
                    data-testid="profile-weight-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="height" className="text-[#A1A1AA] flex items-center gap-2">
                    <Ruler className="w-4 h-4" /> Altura (cm) *
                  </Label>
                  <Input
                    id="height"
                    type="number"
                    placeholder="175"
                    min="100"
                    max="250"
                    value={profile.height}
                    onChange={(e) => setProfile({ ...profile, height: e.target.value })}
                    className="h-12 bg-black/50 border-white/20 rounded-lg"
                    data-testid="profile-height-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Training Info Card */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
                  <Activity className="w-5 h-5 text-[#32CD32]" />
                  Informações de Treino
                </CardTitle>
                <CardDescription className="text-[#A1A1AA]">O teu nível e disponibilidade</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-gray-300">Nível de Experiência *</Label>
                  <Select 
                    value={profile.experience_level} 
                    onValueChange={(value) => setProfile({ ...profile, experience_level: value })}
                  >
                    <SelectTrigger className="h-12 bg-black/50 border-white/20 rounded-lg" data-testid="profile-experience-select">
                      <SelectValue placeholder="Seleciona o teu nível" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/20">
                      <SelectItem value="beginner">Iniciante</SelectItem>
                      <SelectItem value="intermediate">Intermediário</SelectItem>
                      <SelectItem value="advanced">Avançado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-300">Frequência Semanal *</Label>
                  <Select 
                    value={profile.weekly_frequency?.toString()} 
                    onValueChange={(value) => setProfile({ ...profile, weekly_frequency: value })}
                  >
                    <SelectTrigger className="h-12 bg-black/50 border-white/20 rounded-lg" data-testid="profile-frequency-select">
                      <SelectValue placeholder="Dias por semana" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/20">
                      {[1, 2, 3, 4, 5, 6, 7].map((num) => (
                        <SelectItem key={num} value={num.toString()}>
                          {num} {num === 1 ? 'dia' : 'dias'} por semana
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="goals" className="text-[#A1A1AA] flex items-center gap-2">
                    <Target className="w-4 h-4" /> Objetivos *
                  </Label>
                  <Textarea
                    id="goals"
                    placeholder="Ex: Perder peso, melhorar resistência, correr 10km..."
                    value={profile.goals}
                    onChange={(e) => setProfile({ ...profile, goals: e.target.value })}
                    className="min-h-[100px] bg-black/50 border-white/20 rounded-lg resize-none"
                    data-testid="profile-goals-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Health Info Card */}
            <Card className="bg-[#121212] border-white/10 md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
                  <AlertTriangle className="w-5 h-5 text-[#32CD32]" />
                  Informações de Saúde
                </CardTitle>
                <CardDescription className="text-[#A1A1AA]">Lesões e restrições importantes</CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="injuries" className="text-gray-300">
                    Lesões ou Condições Médicas
                  </Label>
                  <Textarea
                    id="injuries"
                    placeholder="Ex: Dor no joelho esquerdo, tendinite..."
                    value={profile.injuries}
                    onChange={(e) => setProfile({ ...profile, injuries: e.target.value })}
                    className="min-h-[100px] bg-black/50 border-white/20 rounded-lg resize-none"
                    data-testid="profile-injuries-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="restrictions" className="text-gray-300">
                    Restrições de Exercício
                  </Label>
                  <Textarea
                    id="restrictions"
                    placeholder="Ex: Não posso correr, evitar impacto alto..."
                    value={profile.restrictions}
                    onChange={(e) => setProfile({ ...profile, restrictions: e.target.value })}
                    className="min-h-[100px] bg-black/50 border-white/20 rounded-lg resize-none"
                    data-testid="profile-restrictions-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Preferred Cardio Card */}
            <Card className="bg-[#121212] border-white/10 md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: 'Barlow Condensed' }}>
                  <Bike className="w-5 h-5 text-[#32CD32]" />
                  Tipos de Cardio Preferidos
                </CardTitle>
                <CardDescription className="text-[#A1A1AA]">Seleciona os tipos de cardio que gostas ou queres praticar</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {Object.entries(CARDIO_TYPES).map(([key, label]) => {
                    const isSelected = profile.preferred_cardio?.includes(key);
                    return (
                      <button 
                        key={key}
                        type="button"
                        className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                          isSelected
                            ? 'bg-[#32CD32]/10 border-[#32CD32]/50'
                            : 'bg-black/30 border-white/10 hover:border-white/30'
                        }`}
                        onClick={() => {
                          const current = profile.preferred_cardio || [];
                          if (current.includes(key)) {
                            setProfile({ ...profile, preferred_cardio: current.filter(c => c !== key) });
                          } else {
                            setProfile({ ...profile, preferred_cardio: [...current, key] });
                          }
                        }}
                        data-testid={`cardio-${key}`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                          isSelected ? 'bg-[#32CD32] border-[#32CD32]' : 'border-white/30'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span className="text-sm">{label}</span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 flex justify-end">
            <Button 
              type="submit" 
              className="btn-primary px-10 py-6 rounded-full text-lg"
              disabled={saving}
              data-testid="profile-save-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 w-5 h-5 animate-spin" />
                  A guardar...
                </>
              ) : (
                <>
                  <Save className="mr-2 w-5 h-5" />
                  Guardar Perfil
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;
